"use client"

import { useState } from "react"
import { DashboardHeader } from "@/components/dashboard/header"
import { PortfolioOverview } from "@/components/dashboard/portfolio-overview"
import { PortfolioChart } from "@/components/dashboard/portfolio-chart"
import { CurrencyPositions } from "@/components/dashboard/currency-positions"
import { AgentPredictions } from "@/components/dashboard/agent-predictions"
import { AgentStatus } from "@/components/dashboard/agent-status"
import { LiveOpenBook } from "@/components/dashboard/live-open-book"
import { RiskMetrics } from "@/components/dashboard/risk-metrics"
import { MaeAnalytics } from "@/components/dashboard/mae-analytics"
import { MarketOverview } from "@/components/dashboard/market-overview"
import { CurrencyPairAnalysis } from "@/components/dashboard/currency-pair-analysis"
import { useAgentDashboardData } from "@/hooks/use-agent-dashboard"
import { Spinner } from "@/components/ui/spinner"

export default function TradingDashboard() {
  const [selectedPair, setSelectedPair] = useState<string | null>(null)
  const {
    portfolio,
    marketRows,
    pairAnalysis,
    agentFeed,
    recentTrades,
    tradeStats,
    transparency,
    agentFleet,
    systemAnalysis,
    portfolioHistory,
    sessionContext,
    maeAnalytics,
    loading,
    error,
  } = useAgentDashboardData()

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader isApiDown={Boolean(error)} sessionContext={sessionContext} />
      
      <main className="min-w-0 max-w-full space-y-6 p-4 sm:p-6">
        {/* Portfolio Metrics */}
        <section className="min-w-0">
          <PortfolioOverview
            portfolio={portfolio}
            tradeItems={recentTrades}
            loading={loading && portfolio === null}
          />
        </section>

        {/* Main Dashboard Grid */}
        <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-3 [&>*]:min-w-0">
          {/* Portfolio Performance Chart - Takes 2 columns */}
          <PortfolioChart
            thoughts={agentFeed}
            transparencyRecords={transparency?.records}
            portfolioHistory={portfolioHistory}
            portfolio={portfolio}
            loading={loading && portfolioHistory.length === 0}
          />

          {/* Agent fleet (swapped with market overview column) */}
          <AgentStatus fleet={agentFleet} loading={loading} />
        </div>

        {/* Currency Pair Analysis + Market overview (swapped with fleet column) */}
        <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-3 [&>*]:min-w-0">
          <CurrencyPairAnalysis
            selectedPair={selectedPair}
            backendAnalysis={pairAnalysis}
            transparencyRecords={transparency?.records}
          />
          <MarketOverview
            selectedPair={selectedPair}
            onSelectPair={setSelectedPair}
            rows={marketRows}
          />
        </div>

        {/* Pair chart + live MT5 book */}
        <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-2 [&>*]:min-w-0">
          <CurrencyPositions selectedPair={selectedPair} />
          <LiveOpenBook portfolio={portfolio} loading={loading && portfolio === null} />
        </div>

        {/* MAE Analytics + Risk */}
        <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-2 [&>*]:min-w-0">
          <MaeAnalytics data={maeAnalytics} loading={loading} />
          <RiskMetrics items={recentTrades} stats={tradeStats} />
        </div>

        {/* Bottom Section: Predictions */}
        <div className="grid min-w-0 grid-cols-1 gap-6 [&>*]:min-w-0">
          <AgentPredictions analysis={systemAnalysis?.analysis ?? null} />
        </div>
      </main>

      {loading && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2.5 rounded-full border border-border bg-background/95 px-4 py-2.5 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/60 animate-in fade-in slide-in-from-bottom-4 duration-300"
        >
          <Spinner className="size-4 text-primary" />
          <span className="text-sm text-muted-foreground">Syncing backend agents...</span>
        </div>
      )}
      {error && !loading && (
        <div
          role="alert"
          className="fixed bottom-6 left-1/2 z-40 flex max-w-[min(90vw,28rem)] -translate-x-1/2 items-center gap-2.5 rounded-full border border-destructive/30 bg-background/95 px-4 py-2.5 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/60 animate-in fade-in slide-in-from-bottom-4 duration-300"
        >
          <span className="text-sm text-destructive">Agent sync warning: {error}</span>
        </div>
      )}
    </div>
  )
}
